import { useTranslation } from 'react-i18next';
import { useLazyDownloadAllAssetEventsQuery } from '@/redux/slices/assetApiSlice';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useCallback, useState } from 'react';
import { Button } from '@electricjs/arc';
import { AssetEventSource } from '@electricjs/core_entity-client';
import { exportCSV } from 'common/utils/exportCsv';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import {
  formatEventDescription,
  formatActionType,
} from '../helpers/formatAssetEventHistory';
import { CategoryMap, EventTypeMap } from './types';
import formatDate from '@/helpers/formatDate';
import { AssetEvent } from '@/types/assets';

type DownloadAllEventsHistoryButtonProps = {
  assetId: string;
};

const getPerformedByValue = (event: AssetEvent, electricLabel: string) => {
  if (event.source === AssetEventSource.Jumpcloud && event.performedBy) {
    return event.performedBy;
  }
  return electricLabel;
};

export const DownloadAllEventsHistoryButton = ({
  assetId,
}: DownloadAllEventsHistoryButtonProps) => {
  const { t } = useTranslation('assets');
  const [isExporting, setIsExporting] = useState(false);
  const [downloadAllAssetEvents] = useLazyDownloadAllAssetEventsQuery();
  const organizationId = useGetOrganizationId();
  const { showSuccessToast, showErrorToast } = useGlobalUI();

  const handleExport = useCallback(async () => {
    setIsExporting(true);

    try {
      const { data: allEvents } = await downloadAllAssetEvents({
        organizationId,
        assetId,
      });

      if (!allEvents) {
        throw new Error('No events data returned');
      }

      const electricLabel = t('eventsHistory.performedBy.electric');

      const csvData = allEvents.map(event => {
        const eventHistoryActionType =
          EventTypeMap[event.actionType] ?? event.actionType.toLowerCase();
        const formattedActionType = formatActionType(eventHistoryActionType);
        const eventHistoryCategory =
          CategoryMap[event.category] ?? event.category;

        const date = formatDate(event.date, {
          year: '2-digit',
          month: '2-digit',
          day: '2-digit',
        });
        const time = formatDate(event.date, {
          hour: '2-digit',
          minute: '2-digit',
        });

        return {
          [t('eventsHistory.columns.event')]: formatEventDescription(event, t),
          [t('eventsHistory.columns.dateTime')]: `${date} - ${time}`,
          [t('eventsHistory.columns.performedBy')]: getPerformedByValue(
            event,
            electricLabel
          ),
          [t('eventsHistory.columns.eventType')]: t(
            `eventsHistory.eventTypes.${formattedActionType}`
          ),
          [t('eventsHistory.columns.category')]: t(
            `eventsHistory.categories.${eventHistoryCategory.toLowerCase()}`
          ),
        };
      });

      exportCSV(csvData, `all-asset-events-${assetId}.csv`);

      showSuccessToast({
        id: 'asset-events-export-success-toast',
        message: t('eventsHistory.exportSuccess'),
        stack: true,
      });
    } catch (error) {
      console.error('Unable to export asset events history', error);
      showErrorToast({
        id: 'asset-events-export-error-toast',
        message: t('eventsHistory.exportError'),
        stack: true,
      });
    } finally {
      setIsExporting(false);
    }
  }, [
    organizationId,
    assetId,
    downloadAllAssetEvents,
    t,
    showSuccessToast,
    showErrorToast,
  ]);

  return (
    <Button
      id="download-all-events-history"
      ml="auto"
      variant="outline"
      iconAfter="download"
      onClick={handleExport}
      loading={isExporting}>
      {t('eventsHistory.export')}
    </Button>
  );
};
